import { EASE } from "@nnf3d/lib";
import type { INITIATIVES_QUERY_RESULT } from "@nnf3d/studio/types";
import { PortableText } from "@portabletext/react";
import { useNavigate } from "@tanstack/react-router";
import { m } from "motion/react";
import { useCallback, useEffect, useMemo } from "react";

import { Icon } from "../components/icon";
import { menuAnimation } from "../lib/const";
import { useGlobal } from "../lib/global-context";
import { useBackEscape } from "../lib/hooks/use-back-escape";
import { useIsMobile } from "../lib/hooks/use-breakpoints";
import type { VoidComponent } from "../lib/types";
import { components } from "./initiative-blocks/components";
import { MobileBreadcrumbs } from "./mobile-breadcrumbs";

interface Props {
	params: { pillar: string; category: string; initiative: string };
	data: INITIATIVES_QUERY_RESULT;
}

const InitiativeRoute: VoidComponent<Props> = ({ params, data }) => {
	const isMobile = useIsMobile();

	useBackEscape({ to: "/$pillar/$category", params });

	const { pillars, exploreInitiativesTitle } = useGlobal();

	useEffect(() => {
		document.querySelector("#menu .no-scrollbar")?.scrollTo(0, 0);
	}, [params]);

	const pillar = useMemo(
		() => pillars.find((p) => p.slug.current === params.pillar),
		[pillars, params.pillar],
	);

	const category = useMemo(
		() => pillar?.categories.find((c) => c.slug.current === params.category),
		[pillar?.categories, params.category],
	);

	const initiatives = useMemo(
		() =>
			category?.initiatives
				.map((i) => data.find((initiative) => initiative._id === i._ref))
				.filter((i) => i !== undefined) ?? [],
		[category?.initiatives, data],
	);

	const initiative = useMemo(
		() => initiatives.find((i) => i.slug.current === params.initiative),
		[initiatives, params.initiative],
	);

	const allInitiatives = useMemo(
		() =>
			initiatives.map((i) => ({
				initiative: i,
				isActive: i.slug.current === params.initiative,
				linkParams: {
					pillar: params.pillar,
					category: params.category,
					initiative: i.slug.current,
				},
			})),
		[initiatives, params.pillar, params.category, params.initiative],
	);

	const navigate = useNavigate();

	const currentInitiativeIndex = useMemo(
		() => allInitiatives.findIndex((i) => i.isActive),
		[allInitiatives],
	);

	const navigateToInitiative = useCallback(
		(index: number) => {
			const wrapped =
				((index % allInitiatives.length) + allInitiatives.length) % allInitiatives.length;
			const target = allInitiatives[wrapped];
			if (target) {
				navigate({ to: "/$pillar/$category/$initiative", params: target.linkParams }).catch(
					console.error,
				);
			}
		},
		[allInitiatives, navigate],
	);

	const handlePrevInitiative = useCallback(() => {
		const current = Math.max(currentInitiativeIndex, 0);
		navigateToInitiative(current - 1);
	}, [currentInitiativeIndex, navigateToInitiative]);

	const handleNextInitiative = useCallback(() => {
		const current = currentInitiativeIndex >= 0 ? currentInitiativeIndex : -1;
		navigateToInitiative(current + 1);
	}, [currentInitiativeIndex, navigateToInitiative]);

	const browseAnimation = {
		initial: { opacity: 0, y: "20%" },
		animate: { opacity: 1, y: "0", transition: { duration: 0.5, ease: EASE.OUT_QUART } },
		exit: { opacity: 0, y: "20%", transition: { duration: 0.1, ease: EASE.IN_QUART } },
	} as const satisfies Parameters<typeof m.div>[0];

	if (!initiative) {
		return null;
	}

	return (
		<>
			<MobileBreadcrumbs params={params} />

			<div className="scroll-container">
				<m.div
					{...menuAnimation(isMobile)}
					className="gap-8 py-3 desktop:w-pane-full-content desktop:py-8"
				>
					<h1 className="mb-[0.5lh] flex-1 text-xl text-foreground desktop:text-3xl">
						{initiative.title}
					</h1>

					<PortableText value={initiative.content} components={components} />
				</m.div>
			</div>

			<m.div {...browseAnimation} className="relative shrink-0">
				<div className="pointer-events-none absolute inset-x-0 bottom-full h-15 bg-linear-to-t from-surface-primary to-transparent" />

				<div className="flex h-14 items-center gap-0.5 text-xs text-foreground desktop:h-18">
					<div className="flex h-14 flex-1 items-center justify-center bg-white p-3 text-center desktop:h-18 desktop:px-8">
						<p className="line-clamp-2 desktop:line-clamp-none">{exploreInitiativesTitle}</p>
					</div>

					<button
						type="button"
						onClick={handlePrevInitiative}
						className="flex aspect-square h-full cursor-pointer items-center justify-center hover-cta p-3"
						aria-label="Previous initiative"
					>
						<Icon name="arrow-left" className="size-5" />
					</button>

					<button
						type="button"
						onClick={handleNextInitiative}
						className="flex aspect-square h-full cursor-pointer items-center justify-center hover-cta p-3"
						aria-label="Next initiative"
					>
						<Icon name="arrow-right" className="size-5" />
					</button>
				</div>
			</m.div>
		</>
	);
};

export { InitiativeRoute };
